import React from 'react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(timezone);
/**
 * Displays a formatted date and time range for a calendar event.
 *
 * @component
 * @param {Object} props
 * @param {dayjs.Dayjs} props.start - The start date/time as a dayjs object.
 * @param {dayjs.Dayjs} props.end - The end date/time as a dayjs object.
 * @param {'timespan'|'datespan'} props.whenType - Type of event span: 'timespan' for time ranges,
 *        'datespan' for all-day/multi-day.
 * @param {string} props.timeFormat - Format string for displaying times (e.g., 'h:mm A').
 * @param {string} props.shortTz - Short timezone label to display (e.g., 'PST').
 * @param {boolean} props.sameDay - Whether the event starts and ends on the same day.
 * @returns {JSX.Element} Formatted date/time display for the event.
 */
export const EventDateTimeDisplay = ({
  start,
  end,
  whenType,
  timeFormat,
  shortTz,
  sameDay,
}) => {
  const startDateString = start.format('dddd, MMMM D');
  // Date span includes next day at 00:00.
  const endDateString = whenType === 'datespan' ? end.subtract(1, 'day').format('dddd, MMMM D') :
    end.format('dddd, MMMM D');
  const startTime = start.format(timeFormat);
  const endTime = end.format(timeFormat);

  if (!sameDay && whenType === 'timespan') {
    return (
      <>
        {startDateString} {startTime} {shortTz}-<br />
        {endDateString} {endTime} {shortTz}
      </>
    );
  }

  if (!sameDay && whenType === 'datespan') {
    return (
      <>
        {startDateString} -<br />
        {endDateString}<br />
        All Day
      </>
    );
  }

  if (whenType === 'timespan') {
    return (
      <>
        {startDateString}<br />
        Start: {startTime} {shortTz}<br />
        End: {endTime} {shortTz}
      </>
    );
  }

  return (
    <>
      {startDateString}<br />
      All Day
    </>
  );
};
